'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

// UI lib dependencies
import {
  Container,
  Group,
  Image,
  Stack,
  Title,
  Button,
  Text,
  Alert,
  Loader,
  LoadingOverlay,
  Paper,
  CopyButton,
  Tooltip,
  Accordion,
} from '@mantine/core';

// UI local components
import UserNote from '@ui-kit/atoms/UserNote';

// Assets
import {
  IconInfoCircle as IconNoTenant,
  IconWifiOff,
  IconCopy,
  IconCheck,
} from '@tabler/icons-react';

// API & types
import { useGetMeQuery, User } from '@app/types/blackroom-api';
/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

/**
 * Main logged-in layout
 */
function PageHome() {
  /* ********************************** HOOKS ********************************* */

  // Auth
  const { loginWithRedirect, isLoading, isAuthenticated } = useAuth0();

  // API
  const {
    data,
    loading: loadingMe,
    error: meError,
  } = useGetMeQuery({
    skip: isLoading || !isAuthenticated,
  });

  // Routing
  const navigate = useNavigate();

  // Translation
  const { t } = useTranslation(['Common', 'GateLayout']);

  /* ***************************** LOCAL VARIABLES **************************** */

  const user = data?.me as User | undefined;

  const registeredUser = !!user?.managedTenant;
  const userHasWorkspaces = (user?.workspaceUsers?.length ?? 0) > 0;

  /* ****************************** SIDE-EFFECTS ****************************** */

  useEffect(() => {
    if (userHasWorkspaces && !user?.managedTenant?.isSuspended) {
      navigate('/workspace/landing');
    }
  }, [userHasWorkspaces, user?.managedTenant?.isSuspended, navigate]);

  useEffect(() => {
    if (!isLoading && isAuthenticated === false) {
      loginWithRedirect();
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  /* ******************************** CALLBACKS ******************************* */

  async function onClickGoToWorkspaces() {
    navigate('/workspace/landing');
  }

  /* ***************************** RENDER HELPERS ***************************** */

  function getTitle() {
    return (
      <Group gap={0} wrap="nowrap">
        {t('Common:brand.name')
          .split('r')
          .map((s: string, i: number) => (i === 1 ? `r${s}` : s))
          .map((c, i) => (
            <Title key={c} order={4} fw={i === 0 ? 700 : 200}>
              {c}
            </Title>
          ))}
      </Group>
    );
  }

  function getGoToWorkspaces() {
    if (!isAuthenticated || loadingMe) {
      return null;
    }
    if (registeredUser) {
      if (user.managedTenant?.isSuspended) {
        return (
          <Alert color="blackroomOrange" icon={<IconNoTenant />}>
            <Text c="blackroomWhite">
              <Trans i18nKey="GateLayout:PageHome.suspendedTenant" />
            </Text>
          </Alert>
        );
      }
      return (
        <Button
          size="lg"
          radius="xs"
          type="submit"
          tt="uppercase"
          onClick={function cb() {
            onClickGoToWorkspaces();
          }}
        >
          {t(
            `GateLayout:PageHome.${
              userHasWorkspaces || registeredUser
                ? 'goToWorkspaces'
                : 'createAccount'
            }`,
          )}
        </Button>
      );
    }
    if (user) {
      return (
        <Alert icon={<IconNoTenant />}>
          <Text c="blackroomWhite">
            <Trans
              i18nKey="GateLayout:PageHome.noTenant"
              components={{ br: <br /> }}
            />
          </Text>
        </Alert>
      );
    }
    return null;
  }

  function getProceedWithExistingUser() {
    if (user) {
      return (
        <UserNote
          user={user}
          note={t('GateLayout:PageHome.greetUser') as string}
        />
      );
    }
    return null;
  }

  function getVerifyingAccount() {
    if (!registeredUser && user) {
      return null;
    }
    if (user?.managedTenant?.isSuspended) {
      return null;
    }
    return (
      <Stack gap="sm" align="center">
        <Text size="md">{t('GateLayout:PageHome.verifying')}</Text>
        <Loader type="bars" size="sm" />
      </Stack>
    );
  }

  /* ******************************** RENDERING ******************************* */

  // Only show network error for actual connection failures (firewall, offline, etc.)
  // Not for HTTP errors like 400, 401, 500 which have statusCode
  const networkError = meError?.networkError;
  const isRealNetworkError = (() => {
    if (!networkError) return false;
    // If error has statusCode, it's an HTTP error response, not a connection failure
    if ('statusCode' in networkError) return false;
    // TypeError or "Failed to fetch" indicates actual network failure
    return (
      networkError instanceof TypeError ||
      networkError.message === 'Failed to fetch'
    );
  })();

  if (isRealNetworkError) {
    const itMessage = t('GateLayout:PageHome.networkError.itMessage');

    return (
      <Container h="100vh" w="100%" fluid>
        <Stack gap="md" h="100%" pt="xl" align="center" justify="center">
          <Alert
            icon={<IconWifiOff size={24} />}
            title={t('GateLayout:PageHome.networkError.title')}
            color="red"
            variant="filled"
            maw={500}
          >
            <Text c="white">
              {t('GateLayout:PageHome.networkError.message')}
            </Text>
            <Accordion
              variant="filled"
              mt="md"
              styles={{
                item: { backgroundColor: 'rgba(255,255,255,0.1)' },
                control: { color: 'white' },
                chevron: { color: 'white' },
                label: { color: 'white', fontWeight: 500 },
                content: { padding: 0 },
              }}
            >
              <Accordion.Item value="it-message">
                <Accordion.Control>
                  {t('GateLayout:PageHome.networkError.copyLabel')}
                </Accordion.Control>
                <Accordion.Panel>
                  <Paper p="sm" bg="white" radius="xs" mt="xs">
                    <Text size="sm" c="dark" style={{ whiteSpace: 'pre-wrap' }}>
                      {itMessage}
                    </Text>
                  </Paper>
                  <CopyButton value={itMessage}>
                    {({ copied, copy }) => (
                      <Tooltip
                        label={
                          copied ? t('Common:cta.copied') : t('Common:cta.copy')
                        }
                      >
                        <Button
                          variant="white"
                          mt="sm"
                          leftSection={
                            copied ? (
                              <IconCheck size={16} />
                            ) : (
                              <IconCopy size={16} />
                            )
                          }
                          onClick={copy}
                        >
                          {copied
                            ? t('Common:cta.copied')
                            : t('GateLayout:PageHome.networkError.copyButton')}
                        </Button>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </Alert>
        </Stack>
      </Container>
    );
  }

  if (!data || (userHasWorkspaces && !user?.managedTenant?.isSuspended)) {
    return (
      <LoadingOverlay
        h="100vh"
        w="100vw"
        bg="white"
        overlayProps={{ blur: 3 }}
        visible
      />
    );
  }

  return (
    <Container h="100vh" w="100%" fluid>
      <Stack gap="xl" h="100%" pt="xl">
        <Group justify="center" gap={0} wrap="nowrap">
          <Image
            alt="Blackroom"
            src="/images/brand/logo.svg"
            width={70}
            height={70}
          />
          {getTitle()}
        </Group>
        <Title order={4} ta="center" pt="xl">
          {t('GateLayout:PageHome.welcomeTitle')}
        </Title>
        {getVerifyingAccount()}
        {getGoToWorkspaces()}
        {getProceedWithExistingUser()}
      </Stack>
    </Container>
  );
}

export default PageHome;
